import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./pages/Login";
import AdminLayout from "./pages/AdminLayout";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import RoutesPage from "./pages/RoutesPage";
import Drivers from "./pages/Drivers";
import Reports from "./pages/Reports";
import DriverApp from "./pages/DriverApp";
import LiveFleet from "./pages/LiveFleet";
import UserManagement from "./pages/UserManagement";
import BillingHub from "./pages/BillingHub";
import IntegrationsSettings from "./pages/IntegrationsSettings";
import DocumentsCenter from "./pages/DocumentsCenter";
import TabletSigningView from "./pages/TabletSigningView";
import CustomerIssues from "./pages/CustomerIssues";
import CustomerIssueDetail from "./pages/CustomerIssueDetail";
import DisposalTickets from "./pages/DisposalTickets";
import DashcamHistory from "./pages/DashcamHistory";
import EmailSignature from "./pages/EmailSignature";
import TemplateDesigner from "./pages/TemplateDesigner";
import DocumentSigner from "./pages/DocumentSigner";
import { Toaster } from "sonner";
import GoogleMapsProvider from "./components/GoogleMapsProvider";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#012340]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    // Visitor / mentor accounts share the admin shell (read-only, redacted).
    if (role === "admin" && user.role === "visitor") return children;
    return <Navigate to={user.role === "driver" ? "/driver" : "/admin"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#012340]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "driver" ? "/driver" : "/admin"} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <GoogleMapsProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* /admin/email-signature is admin-only. The static assets it
                references (/email-assets/*) remain publicly reachable so
                Gmail/Apple Mail/Outlook can load them when recipients
                open a signed email. */}
            <Route
              path="/admin"
              element={
                <Protected role="admin">
                  <AdminLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="routes" element={<RoutesPage />} />
              <Route path="fleet" element={<LiveFleet />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="reports" element={<Reports />} />
              <Route path="billing" element={<BillingHub />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="documents" element={<DocumentsCenter />} />
              <Route path="documents/templates/:tid" element={<TemplateDesigner />} />
              <Route path="documents/:did" element={<DocumentSigner />} />
              <Route path="customer-issues" element={<CustomerIssues />} />
              <Route path="customer-issues/:iid" element={<CustomerIssueDetail />} />
              <Route path="disposal-tickets" element={<DisposalTickets />} />
              <Route path="dashcam" element={<DashcamHistory />} />
              <Route path="email-signature" element={<EmailSignature />} />
            </Route>
            <Route
              path="/driver"
              element={
                <Protected role="driver">
                  <DriverApp />
                </Protected>
              }
            />
            <Route
              path="/sign/:did"
              element={
                <Protected role="admin">
                  <TabletSigningView />
                </Protected>
              }
            />
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </GoogleMapsProvider>
      </AuthProvider>
    </div>
  );
}
